import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTabsModule } from '@angular/material/tabs';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faClose, faFolderBookmark } from '@fortawesome/pro-regular-svg-icons';
import { switchMap, tap } from 'rxjs';

import { SnackbarMode } from '@eiq/ui';

import { IOptionsLibraryFormValue, OptionsLibrarySet } from '../../../models/options-library/options-library-form.interface';
import { OptionsLibraryService } from '../../../services/options-library.service';
import { ConfirmDialogComponent } from '../../confirm-dialog/confirm-dialog.component';
import { OptionsLibraryFormComponent } from '../options-library-form/options-library-form.component';
import { OptionsLibrarySetComponent } from '../options-library-set/options-library-set.component';

@Component({
  standalone: true,
  selector: 'empathy-options-library-picker',
  templateUrl: 'options-library-picker.component.html',
  styleUrl: './options-library-picker.component.scss',
  imports: [
    CommonModule,
    FontAwesomeModule,
    MatButtonModule,
    MatDialogModule,
    MatTabsModule,
    OptionsLibraryFormComponent,
    OptionsLibrarySetComponent,
    MatProgressSpinnerModule
  ]
})
export class OptionsLibraryPickerComponent implements OnInit {
  selectedTab$ = signal<number>(0);
  selectedSet$ = signal<OptionsLibrarySet>(null);

  readonly LibrarySetTab = 0;
  readonly NewSetTab = 1;

  faClose = faClose;
  faFolderBookmark = faFolderBookmark;

  form = new FormGroup({
    name: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    options: new FormControl('', [Validators.required])
  });

  loadingOptionsSets$ = signal(false);
  optionSetForm$ = signal<FormGroup>(this.form);
  optionsSets$ = signal<OptionsLibrarySet[]>([]);

  private dialog = inject(MatDialog);
  private optionsLibraryService = inject(OptionsLibraryService);
  private snackbar = inject(MatSnackBar);

  constructor(private dialogRef: MatDialogRef<OptionsLibraryPickerComponent>) {
    this.fetchOptionsSets();
  }

  ngOnInit(): void {
    this.dialogRef.addPanelClass('empathy-options-picker__dialog');
  }

  onCloseWithOptionsSet(): void {
    this.snackbar.open('Options set was applied successfully.', 'Ok', {
      duration: 3000,
      panelClass: SnackbarMode.Success
    });

    this.dialogRef.close({ optionsSet: this.selectedSet$() });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  onFormatOptionSet(edit: boolean): void {
    const formValue: IOptionsLibraryFormValue = this.optionSetForm$().value;

    if (formValue.options) {
      const splittedOptions = formValue.options
        .split(/\r?\n|\r/)
        .filter((line) => line !== '')
        .map((line, index) => ({
          id: `opt_${index + 1}`,
          value: `${index + 1}`,
          text: line.trim()
        }));

      const optionsLibrarySet: OptionsLibrarySet = {
        name: formValue.name,
        description: formValue.description,
        options: splittedOptions
      };

      if (formValue.id) {
        optionsLibrarySet.id = formValue.id;
      }

      if (!edit) {
        this.optionsLibraryService.saveOptionsSet(optionsLibrarySet).subscribe((result) => {
          if (result) {
            this.snackbar.open('Options added to the library', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Success
            });
            return;
          } else {
            this.snackbar.open('There was an error adding options set to the library. Please, try again later.', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Error
            });
          }
        });
      } else {
        this.optionsLibraryService.editOptionsSet(optionsLibrarySet).subscribe((result) => {
          if (result) {
            this.snackbar.open('Options edited correctly', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Success
            });
            return;
          } else {
            this.snackbar.open('There was an error editing the options set. Please, try again later.', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Error
            });
          }
        });
      }

      this.dialogRef.close({ optionsSet: optionsLibrarySet });
    }
  }

  onEditOptionsSet(set: OptionsLibrarySet): void {
    this.optionSetForm$.set(
      new FormGroup({
        id: new FormControl(set.id, [Validators.required]),
        name: new FormControl(set.name, [Validators.required]),
        description: new FormControl(set.description, [Validators.required]),
        options: new FormControl(set.options.map((option) => option.text).join('\n'), [Validators.required])
      })
    );

    this.selectedTab$.set(this.NewSetTab);
  }

  onDeleteOptionsSet(id: string): void {
    const confirmationRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Remove Options Set',
        confirmText: 'Are you sure you want to remove this set? The options on it will be deleted and cannot be recovered.',
        confirmBtn: 'Remove',
        color: 'warn'
      }
    });

    confirmationRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.optionsLibraryService
          .deleteOptionsSet(id)
          .pipe(
            tap((res) => {
              if (res) {
                this.snackbar.open('Options set was removed successfully.', 'Ok', {
                  duration: 3000,
                  panelClass: SnackbarMode.Success
                });
              } else {
                this.snackbar.open('There was an error removing the options set. Please, try again later.', 'Ok', {
                  duration: 3000,
                  panelClass: SnackbarMode.Error
                });
              }

              this.loadingOptionsSets$.set(true);
            }),
            switchMap(() => this.optionsLibraryService.fetchOptionsSets()),
            tap((res) => {
              this.optionsSets$.set(res);
              this.loadingOptionsSets$.set(false);
            })
          )
          .subscribe();
      }
    });
  }

  onDuplicateOptionsSet(set: OptionsLibrarySet): void {
    const { id: _, ...duplicatedSet } = set;
    this.optionsLibraryService
      .saveOptionsSet(duplicatedSet)
      .pipe(
        tap((res) => {
          if (res) {
            this.snackbar.open('Options set was duplicated successfully.', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Success
            });
          } else {
            this.snackbar.open('There was an error duplicating the options set. Please, try again later.', 'Ok', {
              duration: 3000,
              panelClass: SnackbarMode.Error
            });
          }

          this.loadingOptionsSets$.set(true);
        }),
        switchMap(() => this.optionsLibraryService.fetchOptionsSets()),
        tap((res) => {
          this.optionsSets$.set(res);
          this.loadingOptionsSets$.set(false);
        })
      )
      .subscribe();
  }

  onTabChange(index: number): void {
    if (index === this.LibrarySetTab) {
      this.form.reset();
      this.optionSetForm$.set(this.form);
    }

    this.selectedTab$.set(index);
  }

  private fetchOptionsSets(): void {
    this.loadingOptionsSets$.set(true);
    this.optionsLibraryService.fetchOptionsSets().subscribe((res) => {
      if (res) {
        this.optionsSets$.set(res);
      }

      this.loadingOptionsSets$.set(false);
    });
  }
}
